import type {
    StockQuantity,
    FutureStockQuantities,
    StockMessageValues,
    ProductOrderRestriction,
} from '../types';
import { StockStatuses } from '../types';
import getAvailableStatusMessage from './getAvailableStatusMessage';
import getDisplayedQuantity from './getDisplayedQuantity';
import getMaxDisplayedQuantity from './getMaxDisplayedQuantity';
import getStockStatus from './getStockStatus';
import {
    secondaryStockMessageKey,
    primaryStockMessageKey,
    singleStockMessageKey,
} from './stockMessageKeys';

const getSingleStock = ({
    store,
    stock,
    futureStock,
    desiredQuantity,
    currentStockOnly,
    restriction,
}: {
    store: string;
    stock: StockQuantity;
    futureStock?: FutureStockQuantities;
    desiredQuantity: number;
    currentStockOnly: boolean;
    restriction?: ProductOrderRestriction;
}): { messageId: string; values?: StockMessageValues }[] => {
    const statuses = getStockStatus({
        store,
        stock,
        futureStock,
        desiredQuantity,
        currentStockOnly,
        restriction,
    }).slice(0, 2);

    if (
        statuses.find(
            ({ status }) => StockStatuses.RESTRICTED_FOR_ORDER === status
        )
    ) {
        return [
            {
                messageId: singleStockMessageKey.restrictedForOrder,
            },
        ];
    }

    const availableStatus = statuses.find(
        item => item.status === StockStatuses.AVAILABLE
    );

    if (availableStatus) {
        const primaryStatusMessage = getAvailableStatusMessage(
            availableStatus,
            stock.sku,
            desiredQuantity
        );

        const secondaryStatus = statuses.find(
            item => item.status !== StockStatuses.AVAILABLE
        );

        return [
            primaryStatusMessage,
            ...(secondaryStatus
                ? [
                      {
                          messageId:
                              secondaryStockMessageKey[secondaryStatus.status]!,
                          values: {
                              quantity: secondaryStatus.quantity,
                              date: secondaryStatus.date,
                          },
                      },
                  ]
                : []),
        ];
    }

    const maxDisplayedQuantity = getMaxDisplayedQuantity(stock.sku);

    return statuses.map(({ status, quantity = 0, date }, index) => {
        const displayedQuantity = getDisplayedQuantity({
            quantity,
            maxDisplayedQuantity,
            desiredQuantity,
        });

        if (status === StockStatuses.AVAILABLE_IN_FUTURE && !quantity) {
            return {
                messageId: 'available on {date}',
                values: {
                    date,
                },
            };
        }

        if (index === 0) {
            return {
                messageId: primaryStockMessageKey[status]!,
                values: {
                    quantity: displayedQuantity,
                    date,
                },
            };
        }

        return {
            messageId: secondaryStockMessageKey[status]!,
            values: { quantity, date },
        };
    });
};

export default getSingleStock;
